// src/pages/SearchPage.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { searchQueue } from '../services/searchQueue';
import { useDebounce } from '../hooks/useDebounce';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

type SuggestionType = 'current' | 'category' | 'product' | 'brand' | 'search' | 'popular';
type SearchStatus   = 'idle' | 'loading' | 'slow' | 'done' | 'error';

interface Suggestion {
    id: string;
    text: string;
    type: SuggestionType;
    count?: number;
    categoryId?: number;
    brandName?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Module-level caches  (survive remounts, reset on page reload)
// ─────────────────────────────────────────────────────────────────────────────

interface CacheEntry { suggestions: Suggestion[]; ts: number }
const suggestionsCache   = new Map<string, CacheEntry>();
const categoryCountCache = new Map<number, number>();
const CACHE_TTL = 5 * 60 * 1000; // 5 min

// Singleton lists — loaded once per session
let _popularCache: Suggestion[] = [];
let _recentCache:  Suggestion[] = [];

// ─────────────────────────────────────────────────────────────────────────────
// Pure helpers  (outside component → no stale-closure risk, not recreated)
// ─────────────────────────────────────────────────────────────────────────────

/** Merge recent + popular; recent wins on text collision */
function mergeInitial(recent: Suggestion[], popular: Suggestion[]): Suggestion[] {
    const map = new Map<string, Suggestion>();
    for (const s of recent)  map.set(s.text.toLowerCase(), s);
    for (const s of popular) { if (!map.has(s.text.toLowerCase())) map.set(s.text.toLowerCase(), s); }
    return Array.from(map.values());
}

/**
 * Prepend "current query" entry, dedup the rest, cap total at maxVisible.
 * Ensures the user always sees their own text at position 0 immediately.
 */
function finalize(query: string, raw: Suggestion[], maxVisible = 8): Suggestion[] {
    const qLower  = query.toLowerCase();
    const deduped = raw.filter(s => s.text.toLowerCase() !== qLower);
    const current: Suggestion = { id: `current_${query}`, text: query, type: 'current' };
    return [current, ...deduped.slice(0, maxVisible - 1)];
}

function suggestionIcon(type: SuggestionType): string {
    const map: Record<SuggestionType, string> = {
        current: '🔍', category: '📁', product: '📦',
        brand: '🏷️', search: '🕐', popular: '🔥',
    };
    return map[type] ?? '🔍';
}

function readRecentFromStorage(): Suggestion[] {
    try {
        const raw = localStorage.getItem('recent_searches');
        if (!raw) return [];
        return (JSON.parse(raw) as string[])
            .slice(0, 5)
            .map(h => ({ id: `recent_${h}`, text: h, type: 'search' as const }));
    } catch { return []; }
}

function writeRecentToStorage(text: string): string[] {
    try {
        const raw = localStorage.getItem('recent_searches');
        const history = raw ? (JSON.parse(raw) as string[]) : [];
        const updated = [text, ...history.filter(h => h !== text)].slice(0, 10);
        localStorage.setItem('recent_searches', JSON.stringify(updated));
        return updated;
    } catch { return [text]; }
}

// ─────────────────────────────────────────────────────────────────────────────
// supabase helpers  (pure async, zero React dependencies)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * BFS-based subcategory collector.
 * Avoids await-inside-loop by processing level-by-level.
 */
async function collectCategoryIds(rootId: number): Promise<number[]> {
    const ids   = [rootId];
    const queue = [rootId];
    while (queue.length) {
        const batch = queue.splice(0);
        const { data } = await supabase
            .from('categories').select('id')
            .in('parent_id', batch).eq('is_active', true);
        if (data?.length) {
            for (const c of data) { ids.push(c.id); queue.push(c.id); }
        }
    }
    return ids;
}

async function fetchCategoryCount(catId: number): Promise<number> {
    if (categoryCountCache.has(catId)) return categoryCountCache.get(catId)!;
    const allIds = await collectCategoryIds(catId);
    const { count } = await supabase
        .from('product_categories')
        .select('product_id', { count: 'exact', head: true })
        .in('category_id', allIds);
    const result = count ?? 0;
    categoryCountCache.set(catId, result);
    return result;
}

/** Short-query path: prefix search, lighter load */
async function runQuickSearch(term: string): Promise<Suggestion[]> {
    const [catRes, prodRes] = await Promise.allSettled([
        supabase.from('categories').select('id, name')
            .eq('is_active', true).ilike('name', `${term}%`).limit(3),
        supabase.from('products').select('name')
            .eq('status', 'approved').ilike('name', `${term}%`).limit(4),
    ]);

    const map = new Map<string, Suggestion>();
    if (catRes.status === 'fulfilled') {
        for (const c of catRes.value.data ?? [])
            map.set(c.name.toLowerCase(), { id: `cat_${c.id}`, text: c.name, type: 'category', categoryId: c.id });
    }
    if (prodRes.status === 'fulfilled') {
        for (const p of prodRes.value.data ?? []) {
            const k = p.name.toLowerCase();
            if (!map.has(k)) map.set(k, { id: `prod_${p.name}`, text: p.name, type: 'product' });
        }
    }
    return Array.from(map.values());
}

/** Full-query path: all sources, categories with counts */
async function runFullSearch(term: string): Promise<Suggestion[]> {
    const [catRes, prodRes, brandRes, searchRes] = await Promise.allSettled([
        supabase.from('categories').select('id, name')
            .eq('is_active', true).ilike('name', `%${term}%`).limit(3),
        supabase.from('products').select('name')
            .eq('status', 'approved').ilike('name', `%${term}%`).limit(4),
        supabase.from('products').select('brand')
            .eq('status', 'approved').not('brand', 'is', null).ilike('brand', `%${term}%`).limit(2),
        supabase.from('search_queries').select('query')
            .ilike('query', `%${term}%`).order('results_count', { ascending: false }).limit(3),
    ]);

    const map = new Map<string, Suggestion>();

    // Fetch all category counts in parallel — never await inside a loop
    if (catRes.status === 'fulfilled' && catRes.value.data?.length) {
        const cats   = catRes.value.data;
        const counts = await Promise.all(cats.map(c => fetchCategoryCount(c.id)));
        cats.forEach((c, i) => {
            map.set(c.name.toLowerCase(), {
                id: `cat_${c.id}`, text: c.name, type: 'category', count: counts[i], categoryId: c.id,
            });
        });
    }

    if (prodRes.status === 'fulfilled') {
        for (const p of prodRes.value.data ?? []) {
            const k = p.name.toLowerCase();
            if (!map.has(k)) map.set(k, { id: `prod_${p.name}`, text: p.name, type: 'product' });
        }
    }
    if (brandRes.status === 'fulfilled') {
        for (const b of brandRes.value.data ?? []) {
            if (!b.brand) continue;
            const k = b.brand.toLowerCase();
            if (!map.has(k)) map.set(k, { id: `brand_${b.brand}`, text: b.brand, type: 'brand', brandName: b.brand });
        }
    }
    if (searchRes.status === 'fulfilled') {
        for (const s of searchRes.value.data ?? []) {
            const k = s.query.toLowerCase();
            if (!map.has(k)) map.set(k, { id: `search_${s.query}`, text: s.query, type: 'search' });
        }
    }

    // Stable sort: categories first, then preserve insertion order
    return Array.from(map.values()).sort((a, b) => {
        if (a.type === 'category' && b.type !== 'category') return -1;
        if (b.type === 'category' && a.type !== 'category') return  1;
        return 0;
    });
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export const SearchPage: React.FC = () => {
    const navigate = useNavigate();
    const inputRef = useRef<HTMLInputElement>(null);

    const [query,           setQuery]           = useState('');
    const [suggestions,     setSuggestions]     = useState<Suggestion[]>([]);
    const [status,          setStatus]          = useState<SearchStatus>('idle');
    const [selectedIndex,   setSelectedIndex]   = useState(-1);
    const [popularSearches, setPopularSearches] = useState<Suggestion[]>(_popularCache);
    const [recentSearches,  setRecentSearches]  = useState<Suggestion[]>(_recentCache);

    /**
     * ┌──────────────────────────────────────────────────────────────────────┐
     * │  BUG FIX — use debouncedQuery (not query) as the effect dependency.  │
     * │                                                                       │
     * │  query  → drives the controlled <input> (instant, no debounce)       │
     * │  debouncedQuery → drives ALL supabase fetches                         │
     * │                                                                       │
     * │  Using `query` inside the fetch effect means the effect fires on      │
     * │  every keystroke AND the closure captures the value at schedule-time, │
     * │  which may already be stale by the time await resolves.               │
     * └──────────────────────────────────────────────────────────────────────┘
     */
    const debouncedQuery = useDebounce(query, 150);

    /**
     * Race-condition guard (version counter).
     *
     * WHY NOT AbortController?
     * supabase JS v2 internally ignores abort signals — the HTTP request
     * completes regardless and the promise resolves.  A version counter is
     * the only reliable guard: we increment before every fetch and check
     * after every await whether we're still the "current" request.
     */
    const reqIdRef    = useRef(0);

    /**
     * Slow-connection timer stored as a ref (NOT state).
     *
     * BUG in original code: the timer callback read `searchProgress` state
     * from the closure captured at setTimeout-call-time.  By the time the
     * timer fires (1500 ms later) that captured value is always stale.
     * Fix: the callback only compares reqIdRef — a ref always reads current.
     */
    const slowTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // ── Mount: prefetch popular + load recent ─────────────────────────────

    useEffect(() => {
        inputRef.current?.focus();

        const init = async () => {
            // Recent searches — synchronous, no network
            if (_recentCache.length === 0) _recentCache = readRecentFromStorage();
            setRecentSearches([..._recentCache]);

            // Popular searches — one network call, module-level cached forever
            if (_popularCache.length === 0) {
                const { data } = await supabase
                    .from('search_queries').select('query')
                    .order('results_count', { ascending: false }).limit(8);
                if (data?.length) {
                    _popularCache = data.map(p => ({
                        id: `popular_${p.query}`, text: p.query, type: 'popular' as const,
                    }));
                }
            }
            setPopularSearches([..._popularCache]);
        };

        init();
    }, []);

    // ── Core suggestions effect ──────────────────────────────────────────

    useEffect(() => {
        const term = debouncedQuery.trim();

        // ── Case 1: empty input → show merged initial list, no network ────
        if (term.length === 0) {
            if (slowTimerRef.current) clearTimeout(slowTimerRef.current);
            setSuggestions(mergeInitial(_recentCache, _popularCache));
            setStatus('idle');
            return;
        }

        // ── Assign version to this fetch ──────────────────────────────────
        const reqId = ++reqIdRef.current;
        const isCurrent = () => reqId === reqIdRef.current;

        // ── Case 2: cache hit — zero network, zero flicker ────────────────
        const cached = suggestionsCache.get(term);
        if (cached && Date.now() - cached.ts < CACHE_TTL) {
            if (slowTimerRef.current) clearTimeout(slowTimerRef.current);
            setSuggestions(cached.suggestions);
            setStatus('done');
            return;
        }

        // ── Case 3: network fetch ─────────────────────────────────────────
        setStatus('loading');

        // Slow-connection banner — fires only if THIS request is still active
        if (slowTimerRef.current) clearTimeout(slowTimerRef.current);
        slowTimerRef.current = setTimeout(() => {
            if (isCurrent()) setStatus('slow');
        }, 1500);

        const run = async () => {
            try {
                const raw = term.length <= 2
                    ? await runQuickSearch(term)
                    : await runFullSearch(term);

                // Guard #1 — after main network round-trip
                if (!isCurrent()) return;

                const final = finalize(term, raw);
                suggestionsCache.set(term, { suggestions: final, ts: Date.now() });

                // Guard #2 — before writing to state
                if (!isCurrent()) return;

                setSuggestions(final);
                setStatus('done');
            } catch (err) {
                if (isCurrent()) {
                    console.error('[SearchPage]', err);
                    setStatus('error');
                }
            } finally {
                // Cancel slow-timer only for the request that owns it
                if (isCurrent() && slowTimerRef.current) {
                    clearTimeout(slowTimerRef.current);
                }
            }
        };

        run();

        /*
         * Deps: only debouncedQuery.
         *
         * recentSearches / popularSearches are intentionally excluded:
         *   • When term === '' the effect uses _recentCache / _popularCache
         *     (module refs) which are always current — no re-run needed.
         *   • Including the state arrays would cause extra re-runs after
         *     history mutations while a fetch is already in-flight.
         */
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedQuery]);

    // ── History helpers ───────────────────────────────────────────────────

    const persistRecent = useCallback((text: string) => {
        const history = writeRecentToStorage(text);
        _recentCache = history.slice(0, 5).map(h => ({
            id: `recent_${h}`, text: h, type: 'search' as const,
        }));
        setRecentSearches([..._recentCache]);
        if (text.trim().length >= 3) searchQueue.addToQueue(text, 0);
    }, []);

    const clearRecent = useCallback(() => {
        try { localStorage.removeItem('recent_searches'); } catch { /* noop */ }
        _recentCache = [];
        setRecentSearches([]);
    }, []);

    const removeRecent = useCallback((text: string, e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            const raw  = localStorage.getItem('recent_searches');
            const list = raw ? (JSON.parse(raw) as string[]).filter(h => h !== text) : [];
            localStorage.setItem('recent_searches', JSON.stringify(list));
            _recentCache = list.slice(0, 5).map(h => ({ id: `recent_${h}`, text: h, type: 'search' as const }));
            setRecentSearches([..._recentCache]);
        } catch { /* noop */ }
    }, []);

    // ── Navigation ────────────────────────────────────────────────────────

    const performSearch = useCallback((q: string, categoryId?: number, brand?: string) => {
        persistRecent(q);
        const params = new URLSearchParams();
        if (categoryId)  params.set('category', String(categoryId));
        else if (brand)  params.set('brand', brand);
        else if (q)      params.set('search', q);
        const qs = params.toString();
        navigate(qs ? `/products?${qs}` : '/products');
    }, [persistRecent, navigate]);

    const handleSuggestionClick = useCallback((s: Suggestion) => {
        if      (s.type === 'category' && s.categoryId) performSearch(s.text, s.categoryId);
        else if (s.type === 'brand'    && s.brandName)  performSearch(s.brandName, undefined, s.brandName);
        else                                            performSearch(s.text);
    }, [performSearch]);

    // ── Keyboard navigation ───────────────────────────────────────────────

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex(i => Math.min(i + 1, suggestions.length - 1));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex(i => Math.max(i - 1, -1));
                break;
            case 'Enter':
                e.preventDefault();
                if (selectedIndex >= 0 && suggestions[selectedIndex]) {
                    handleSuggestionClick(suggestions[selectedIndex]);
                } else if (query.trim()) {
                    performSearch(query.trim());
                }
                break;
            case 'Escape':
                navigate(-1);
                break;
        }
    }, [suggestions, selectedIndex, query, handleSuggestionClick, performSearch, navigate]);

    // ── Derived UI flags (no extra state → no extra renders) ─────────────

    const isLoading        = status === 'loading' || status === 'slow';
    const isSlowConnection = status === 'slow';

    // Don't show spinner when we already have stale suggestions to display
    // (avoids the list "disappearing" while the next fetch is in-flight)
    const showSpinner  = isLoading && suggestions.length <= 1;
    const showEmpty    = status === 'done' && debouncedQuery.trim().length >= 2 && suggestions.length === 0;
    const showError    = status === 'error';

    // Use raw `query` (not debounced) so initial sections vanish immediately
    // on the first keystroke — no 150 ms lag before they hide.
    const showInitial  = query.trim().length === 0 && !isLoading;

    // ── Render ────────────────────────────────────────────────────────────

    return (
        <div className="search-page">

            {/* Header */}
            <div className="search-header">
                <div className={`search-input-wrapper${isSlowConnection ? ' search-input-wrapper--slow' : ''}`}>
                    <span className="search-icon" aria-hidden>
                        {isLoading ? <span className="spinner-inline" /> : '🔍'}
                    </span>

                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={e => { setQuery(e.target.value); setSelectedIndex(-1); }}
                        onKeyDown={handleKeyDown}
                        placeholder="Поиск товаров..."
                        className="search-page-input"
                        autoComplete="off"
                        autoFocus
                        aria-label="Поиск товаров"
                        aria-autocomplete="list"
                        aria-expanded={suggestions.length > 0}
                    />

                    {query.length > 0 && (
                        <button
                            className="search-clear-btn"
                            onClick={() => { setQuery(''); setSelectedIndex(-1); inputRef.current?.focus(); }}
                            aria-label="Очистить поиск"
                        >✕</button>
                    )}
                </div>

                <button className="search-cancel" onClick={() => navigate(-1)}>
                    Отмена
                </button>
            </div>

            {/* Slow-connection progress bar */}
            {isSlowConnection && (
                <div className="search-slow-bar" role="status" aria-live="polite">
                    <div className="search-slow-bar__fill" />
                    <span className="search-slow-bar__label">🌐 Медленное соединение…</span>
                </div>
            )}

            {/* Content */}
            <div className="search-content">

                {/* Spinner — only when no previous list is displayed */}
                {showSpinner && (
                    <div className="search-spinner-wrap" aria-busy="true" aria-live="polite">
                        <div className="search-spinner" />
                        <p className="search-spinner__label">
                            {isSlowConnection ? 'Медленное соединение, ищем…' : 'Поиск…'}
                        </p>
                    </div>
                )}

                {/* Error state */}
                {showError && (
                    <div className="search-error" role="alert">
                        <span aria-hidden>⚠️</span>
                        <p>Не удалось выполнить поиск</p>
                        <button
                            className="search-error__retry"
                            onClick={() => {
                                // Invalidate any in-flight request, then re-trigger
                                reqIdRef.current++;
                                setStatus('idle');
                                // Force debouncedQuery to re-evaluate by toggling query
                                setQuery(q => q + ' ');
                                setTimeout(() => setQuery(q => q.trimEnd()), 10);
                            }}
                        >Повторить</button>
                    </div>
                )}

                {/* Empty state */}
                {showEmpty && (
                    <div className="search-empty" role="status">
                        <span className="search-empty__icon" aria-hidden>🔍</span>
                        <p>Ничего не найдено</p>
                        <p className="search-empty__hint">Попробуйте изменить запрос</p>
                    </div>
                )}

                {/* Suggestions list */}
                {suggestions.length > 0 && (
                    <div className="search-suggestions" role="listbox" aria-label="Подсказки">
                        {suggestions.map((s, idx) => (
                            <div
                                key={s.id}
                                role="option"
                                aria-selected={idx === selectedIndex}
                                className={[
                                    'suggestion-item',
                                    s.type === 'current'     ? 'suggestion-item--current' : '',
                                    idx === selectedIndex    ? 'suggestion-item--active'  : '',
                                ].filter(Boolean).join(' ')}
                                onClick={() => handleSuggestionClick(s)}
                                onMouseEnter={() => setSelectedIndex(idx)}
                            >
                                <span className="suggestion-item__icon" aria-hidden>
                                    {suggestionIcon(s.type)}
                                </span>
                                <span className="suggestion-item__text">{s.text}</span>
                                {s.type === 'category' && s.count != null && (
                                    <span className="suggestion-item__count">{s.count} товаров</span>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Initial sections: shown only when input is empty */}
                {showInitial && (
                    <>
                        {recentSearches.length > 0 && (
                            <section className="search-section" aria-label="Недавние запросы">
                                <div className="search-section__header">
                                    <span>Недавние запросы</span>
                                    <button className="search-section__clear" onClick={clearRecent}>
                                        Очистить
                                    </button>
                                </div>
                                {recentSearches.map(s => (
                                    <div
                                        key={s.id}
                                        role="option"
                                        className="history-item"
                                        onClick={() => handleSuggestionClick(s)}
                                    >
                                        <span className="history-item__icon" aria-hidden>🕐</span>
                                        <span className="history-item__text">{s.text}</span>
                                        <button
                                            className="history-item__remove"
                                            onClick={e => removeRecent(s.text, e)}
                                            aria-label={`Удалить «${s.text}» из истории`}
                                        >✕</button>
                                    </div>
                                ))}
                            </section>
                        )}

                        {popularSearches.length > 0 && (
                            <section className="search-section" aria-label="Популярные запросы">
                                <div className="search-section__header">
                                    <span>Популярные запросы</span>
                                </div>
                                <div className="popular-grid">
                                    {popularSearches.map(s => (
                                        <div
                                            key={s.id}
                                            role="option"
                                            className="popular-item"
                                            onClick={() => handleSuggestionClick(s)}
                                        >
                                            🔥 {s.text}
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};
